/**
 * The languages the site itself is written in, and how a URL carries one.
 *
 * This is deliberately separate from `LanguageCode`, which is the set of
 * languages the course teaches. The two lists happen to hold the same three
 * entries today and they answer different questions: one decides what a sheet
 * contains, the other decides what the page around it says. Keeping them apart
 * is what lets a Dutch speaker read a Thai-to-English sheet.
 */

export const LOCALES = ["en", "nl", "th"] as const;
export type Locale = (typeof LOCALES)[number];

/**
 * English is the default, and the default is the one that keeps the bare paths.
 *
 * `/articles` was indexed before the site spoke anything else, so English stays
 * where it is and the other two take a prefix. Moving English under `/en` would
 * turn every indexed URL into a redirect for no gain a reader would notice.
 */
export const DEFAULT_LOCALE: Locale = "en";

/**
 * The `lang` attribute and the `hreflang` value, which must be the same string:
 * a document claiming `lang="en-GB"` and an alternate claiming `hreflang="en"`
 * are two different declarations about one page.
 *
 * English is tagged `en-GB` because the copy is British ("practise",
 * "romanisation"). Dutch and Thai carry no region: the copy is not written for
 * Belgium or against it, and a region a page does not earn narrows who Google
 * shows it to.
 */
export const LOCALE_TAGS: Record<Locale, string> = {
  en: "en-GB",
  nl: "nl",
  th: "th",
};

/** Open Graph wants language_TERRITORY, so here a region has to be invented. */
export const OG_LOCALES: Record<Locale, string> = {
  en: "en_GB",
  nl: "nl_NL",
  th: "th_TH",
};

/** How each language names itself, which is how a switcher must name it. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  nl: "Nederlands",
  th: "ไทย",
};

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/**
 * The path a canonical English route takes in another language. Paths are
 * written once, in English, and every locale's URL is derived from them.
 */
export function localePath(locale: Locale, path: string): string {
  if (locale === DEFAULT_LOCALE) return path;
  return path === "/" ? `/${locale}` : `/${locale}${path}`;
}

/**
 * The inverse: which locale a URL is in, and what it would be in English.
 *
 * A path that starts with a locale segment is in that locale, and everything
 * else is English. `/nl` alone is the Dutch homepage, so it maps back to `/`.
 */
export function parseLocalePath(pathname: string): {
  locale: Locale;
  path: string;
} {
  const match = /^\/([^/]+)(\/.*)?$/.exec(pathname);
  const segment = match?.[1];

  if (segment && isLocale(segment) && segment !== DEFAULT_LOCALE) {
    return { locale: segment, path: match?.[2] || "/" };
  }

  return { locale: DEFAULT_LOCALE, path: pathname || "/" };
}
